// Presence: what we send (throttled), and how we read what peers send. Presence is free-form on the
// wire, so every reader validates the shape. Pure; clock and timers are injected.
import type { Position } from "./graph.ts";
import type { RemotePeer } from "./sync.ts";

export type PresenceState = {
  cursor?: Position | null;
  selection?: string[];
  dragging?: { id: string; position: Position }[];
};

export interface Timers {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (h: unknown) => void;
}

export const PRESENCE_INTERVAL_MS = 66; // ~15 per second

export const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Merges updates into one presence object and sends it at most every `intervalMs`: the first change goes
 * out at once, later ones coalesce into a trailing send. Identical states are not sent twice.
 */
export class PresenceSender {
  private state: PresenceState = {};
  private lastSent: PresenceState | null = null;
  private lastAt = Number.NEGATIVE_INFINITY;
  private timer: unknown = null;
  private readonly send: (p: PresenceState) => void;
  private readonly t: Timers;
  private readonly interval: number;

  constructor(send: (p: PresenceState) => void, timers: Timers, intervalMs = PRESENCE_INTERVAL_MS) {
    this.send = send;
    this.t = timers;
    this.interval = intervalMs;
  }

  update(patch: PresenceState): void {
    this.state = { ...this.state, ...patch };
    this.schedule();
  }

  /** Forget what the peer room knows (new connection): the next update sends again. */
  reset(): void {
    this.lastSent = null;
    this.lastAt = Number.NEGATIVE_INFINITY;
    this.cancel();
  }

  cancel(): void {
    if (this.timer !== null) this.t.clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    if (this.timer !== null) return;
    const wait = this.lastAt + this.interval - this.t.now();
    if (wait <= 0) {
      this.flush();
      return;
    }
    this.timer = this.t.setTimeout(() => {
      this.timer = null;
      this.flush();
    }, wait);
  }

  private flush(): void {
    if (this.lastSent && same(this.lastSent, this.state)) return;
    this.lastSent = this.state;
    this.lastAt = this.t.now();
    this.send(this.state);
  }
}

// ─── reading peers' presence ─────────────────────────────────────────────────

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isPos = (v: unknown): v is Position =>
  !!v && typeof v === "object" && isNum((v as Position).x) && isNum((v as Position).y);

export interface RemoteCursor {
  peerId: string;
  name: string;
  color: string;
  x: number;
  y: number;
}

export function remoteCursors(peers: Iterable<RemotePeer>): RemoteCursor[] {
  const out: RemoteCursor[] = [];
  for (const p of peers) {
    const c = p.presence?.cursor;
    if (isPos(c)) out.push({ peerId: p.id, name: p.name, color: p.color, x: c.x, y: c.y });
  }
  return out;
}

/** node id → colours of the peers who have it selected. */
export function remoteSelections(peers: Iterable<RemotePeer>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const p of peers) {
    const sel = p.presence?.selection;
    if (!Array.isArray(sel)) continue;
    for (const id of sel) {
      if (typeof id !== "string") continue;
      const list = out.get(id);
      if (list) list.push(p.color);
      else out.set(id, [p.color]);
    }
  }
  return out;
}

/** node id → live position while a peer drags it (the last peer wins on a clash). */
export function remoteDrags(
  peers: Iterable<RemotePeer>,
): Map<string, { position: Position; color: string; name: string }> {
  const out = new Map<string, { position: Position; color: string; name: string }>();
  for (const p of peers) {
    const d = p.presence?.dragging;
    if (!Array.isArray(d)) continue;
    for (const item of d) {
      if (!item || typeof item !== "object") continue;
      const { id, position } = item as { id?: unknown; position?: unknown };
      if (typeof id === "string" && isPos(position)) out.set(id, { position, color: p.color, name: p.name });
    }
  }
  return out;
}

/** Distinct people (one user with two tabs is one avatar). */
export function uniquePeople(peers: Iterable<RemotePeer>): RemotePeer[] {
  const seen = new Map<string, RemotePeer>();
  for (const p of peers) if (!seen.has(p.userId)) seen.set(p.userId, p);
  return [...seen.values()];
}

// ─── reconnect ───────────────────────────────────────────────────────────────

/** Exponential backoff with jitter: 0.5 s, 1 s, 2 s … capped at 15 s. `rand` in [0,1). */
export function backoffMs(attempt: number, rand: number = Math.random()): number {
  const base = Math.min(15_000, 500 * 2 ** Math.max(0, attempt));
  return Math.round(base * (0.75 + rand * 0.5));
}

/** Close codes after which retrying is pointless (unauthorized/forbidden/not found/gone). */
export function shouldReconnect(code: number): boolean {
  return ![4403, 4404, 4410].includes(code);
}
